import { useSearchParams } from 'react-router';
import { useMemo, useCallback } from 'react';
import { isEmpty } from 'lodash';

type QueryParamValue<T> = T extends Array<infer U> ? U[] : T;

function useQueryParam<
T extends string | string[] | number | number[] | (string | null)[] | undefined
>(
  key: string,
  defaultValue?: T
): [
  QueryParamValue<T>,
  (newValue: T | null, updater?: (params: URLSearchParams) => void) => void
] {
  const [searchParams, setSearchParams] = useSearchParams();

  const value = useMemo(() => {
    const paramValues = searchParams.getAll(key);
    const uniqueValues = Array.from(new Set(paramValues));

    if (!isEmpty(uniqueValues)) {
      if (uniqueValues.length > 1 || Array.isArray(defaultValue)) {
        return uniqueValues as QueryParamValue<T>;
      } else {
        return uniqueValues[0] as QueryParamValue<T>;
      }
    }

    return defaultValue as QueryParamValue<T>;
  }, [key, searchParams, defaultValue]);

  const setValue = useCallback(
    (newValue: T | null, updater?: (params: URLSearchParams) => void) => {
      const newSearchParams = new URLSearchParams(searchParams);

      if (newValue === null || newValue === undefined) {
        newSearchParams.delete(key);
      } else if (Array.isArray(newValue)) {
        newSearchParams.delete(key);
        const uniqueValues = Array.from(new Set(newValue));
        uniqueValues.forEach((val) => newSearchParams.append(key, String(val)));
      } else {
        newSearchParams.set(key, String(newValue));
      }

      if (updater) {
        updater(newSearchParams);
      }

      if (newSearchParams.toString() !== searchParams.toString()) {
        setSearchParams(newSearchParams);
      }
    
    },
    [key, searchParams, setSearchParams]
  );

  return [value, setValue];
}

export default useQueryParam;
